import { apiClient as api } from "./httpClient";

// Janelas de acesso restrito (RN-012): períodos em que só os usuários da lista entram. O bloqueio
// acontece no LOGIN — quem é barrado não recebe token, então não há tela para interceptar nem
// escrita para frear.
//
// O corte de sessões (RN-012a) é o "desligar todo mundo": derruba token JÁ emitido, que a
// restrição de login não alcança porque o JWT vale até expirar.

export type AccessRestrictionStatus = "vigente" | "agendada" | "encerrada" | "inativa";

export interface AccessRestrictionItem {
  id: string;
  /** ISO em UTC. Obrigatório: para valer já, a tela pré-preenche com "agora". */
  startsAt: string;
  endsAt: string;
  message: string;
  allowedUserIds: string[];
  allowedCount: number;
  /** Derivado do relógio no servidor, não guardado: uma janela vira "encerrada" sozinha. */
  status: AccessRestrictionStatus;
  active: boolean;
}

export interface AccessRestrictionCandidate {
  id: string;
  name: string;
  email: string;
  /** Ainda não criou a senha pelo convite — é quem falta antes da abertura. */
  passwordPending: boolean;
  /** Último LOGIN (ISO, UTC). Renovado no login, não na atividade. */
  lastAccessAt: string | null;
  /** Conta de serviço (monitoramento): o servidor a inclui em toda janela; a tela trava o visto. */
  isServiceAccount: boolean;
}

export interface AccessRestrictionPage {
  items: AccessRestrictionItem[];
  candidates: AccessRestrictionCandidate[];
  sessionsRevokedAt: string | null;
}

export interface SaveAccessRestrictionPayload {
  startsAt: string;
  endsAt: string;
  message: string;
  allowedUserIds: string[];
}

// A API devolve datas "ingênuas" (sem Z) que SÃO instantes em UTC. Sem a marca, `new Date()`
// as lê no fuso do navegador: a janela aparecia 3h à frente e cada edição gravava +3h (achado
// do Hilton em prd, 04/09/2026: "é problema de UTC"). Marca-se aqui, na borda, uma vez só.
const asUtc = (value: string | null): string | null =>
  value && !/(Z|[+-]\d{2}:\d{2})$/.test(value) ? `${value}Z` : value;

export const accessRestrictionService = {
  list: async (entityId: string): Promise<AccessRestrictionPage> => {
    const response = await api.get(`/Entities/${entityId}/access-restrictions`);
    const data = response.data as AccessRestrictionPage;
    return {
      ...data,
      items: data.items.map((i) => ({ ...i, startsAt: asUtc(i.startsAt) as string, endsAt: asUtc(i.endsAt) as string })),
      candidates: data.candidates.map((c) => ({ ...c, lastAccessAt: asUtc(c.lastAccessAt) })),
      sessionsRevokedAt: asUtc(data.sessionsRevokedAt),
    };
  },

  create: async (entityId: string, payload: SaveAccessRestrictionPayload): Promise<void> => {
    await api.post(`/Entities/${entityId}/access-restrictions`, payload);
  },

  update: async (entityId: string, id: string, payload: SaveAccessRestrictionPayload): Promise<void> => {
    await api.put(`/Entities/${entityId}/access-restrictions/${id}`, payload);
  },

  setActive: async (entityId: string, id: string, active: boolean): Promise<void> => {
    await api.patch(`/Entities/${entityId}/access-restrictions/${id}/active`, { active });
  },

  /** Devolve um token NOVO para quem executou: todo mundo cai, o operador continua dentro. */
  revokeSessions: async (entityId: string): Promise<string> => {
    const response = await api.post(`/Entities/${entityId}/access-restrictions/revoke-sessions`);
    return response.data.token;
  },
};
